import ContentCutIcon from "@mui/icons-material/ContentCut";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FlightIcon from "@mui/icons-material/Flight";
import MemoryIcon from "@mui/icons-material/Memory";
import PetsIcon from "@mui/icons-material/Pets";
import PublicIcon from "@mui/icons-material/Public";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";

export const CERT_STORAGE_KEY = "vetinstant_custom_certificates";

export const DEFAULT_FOOTER_NOTE = "";

/** Fields collected in the generate-certificate modal, not on Edit. */
export const CERT_MODAL_FIELDS = {
  "std-fitness": [
    "Purpose",
    "Temperature",
    "Pulse",
    "Respiration",
    "Body Condition",
  ],
  "std-fit-travel": ["Mode of Travel", "Findings", "Valid Until"],
  "std-rabies": [
    "Vaccine (Brand)",
    "Batch No.",
    "Next Due Date",
    "Administering Vet",
  ],
  "std-travel-export": [
    "Destination Country",
    "Mode of Transport",
    "Rabies Vaccination",
    "Parasite Treatment",
    "Microchip No.",
  ],
  "std-sterilization": ["Procedure", "Method", "Operating Vet"],
  "std-euthanasia": ["Reason", "Method", "Disposition of Remains"],
  "std-microchip": [
    "Microchip No.", //(ISO 11784/11785)
    "Site of Implantation",
    "Manufacturer / Brand",
  ],
};

const certOpt = (name) => ({ name, value: name });

export const CERT_GENERATE_FIELDS = {
  "std-fitness": [
    {
      key: "purpose",
      label: "PURPOSE",
      type: "select",
      options: [
        certOpt("Boarding"),
        certOpt("Travel"),
        certOpt("Insurance"),
        certOpt("Sale"),
        certOpt("Show / Event"),
        certOpt("Adoption"),
      ],
    },
    { key: "temperature", label: "TEMPERATURE", type: "text" },
    { key: "pulse", label: "PULSE", type: "text" },
    { key: "respiration", label: "RESPIRATION", type: "text" },
    {
      key: "bodyCondition",
      label: "BODY CONDITION",
      type: "select",
      options: [
        certOpt("Emaciated (1/9)"),
        certOpt("Underweight (1–3/9)"),
        certOpt("Ideal (4–5/9)"),
        certOpt("Overweight (6–7/9)"),
        certOpt("Obese (8–9/9)"),
      ],
    },
  ],
  "std-fit-travel": [
    {
      key: "modeOfTravel",
      label: "MODE OF TRAVEL",
      type: "select",
      options: [
        certOpt("Domestic Air Travel (Cabin)"),
        certOpt("Domestic Air Travel (Hold)"),
        certOpt("International Air Travel"),
        certOpt("Road"),
        certOpt("Rail"),
        certOpt("Sea"),
      ],
    },
    { key: "findings", label: "FINDINGS", type: "textarea" },
    { key: "validUntil", label: "VALID UNTIL", type: "date" },
  ],
  "std-rabies": [
    { key: "vaccineBrand", label: "VACCINE (BRAND)", type: "text" },
    { key: "batchNo", label: "BATCH NO.", type: "text" },
    { key: "nextDueDate", label: "NEXT DUE DATE", type: "date" },
    { key: "administeringVet", label: "ADMINISTERING VET", type: "vet" },
  ],
  "std-travel-export": [
    {
      key: "destinationCountry",
      label: "DESTINATION COUNTRY",
      type: "country",
    },
    {
      key: "modeOfTransport",
      label: "MODE OF TRANSPORT",
      type: "select",
      options: [
        certOpt("Air Cargo"),
        certOpt("Cabin"),
        certOpt("Checked Baggage"),
        certOpt("Road"),
        certOpt("Sea"),
      ],
    },
    { key: "rabiesVaccination", label: "RABIES VACCINATION", type: "text" },
    { key: "parasiteTreatment", label: "PARASITE TREATMENT", type: "text" },
    { key: "microchipNo", label: "MICROCHIP NO.", type: "text" },
  ],
  "std-sterilization": [
    {
      key: "procedure",
      label: "PROCEDURE",
      type: "select",
      options: [
        certOpt("Orchiectomy (Neuter)"),
        certOpt("Ovariohysterectomy (Spay)"),
        certOpt("Laparoscopic ovariectomy"),
        certOpt("Laparoscopic castration"),
      ],
    },
    {
      key: "method",
      label: "METHOD",
      type: "select",
      options: [
        certOpt("Standard surgical castration"),
        certOpt("Ovariohysterectomy (spay)"),
        certOpt("Laparoscopic sterilization"),
      ],
    },
    { key: "operatingVet", label: "OPERATING VET", type: "vet" },
  ],
  "std-euthanasia": [
    { key: "reason", label: "REASON", type: "textarea" },
    { key: "method", label: "METHOD", type: "text" },
    {
      key: "dispositionOfRemains",
      label: "DISPOSITION OF REMAINS",
      type: "select",
      options: [
        certOpt("Returned to owner"),
        certOpt("Cremation"),
        certOpt("Clinic disposal"),
      ],
    },
  ],
  "std-microchip": [
    {
      key: "microchipNo",
      label: "MICROCHIP NO.", // (ISO 11784/11785)
      type: "text",
    },
    {
      key: "siteOfImplantation",
      label: "SITE OF IMPLANTATION",
      type: "select",
      options: [
        certOpt("Subcutaneous, dorsal midline between scapulae"),
        certOpt("Subcutaneous, left side of neck"),
        certOpt("Subcutaneous, right side of neck"),
      ],
    },
    {
      key: "manufacturerBrand",
      label: "MANUFACTURER / BRAND",
      type: "text",
    },
  ],
};

export const findStandardTemplateByName = (name) =>
  STANDARD_CERTIFICATES.find(
    (cert) =>
      String(cert?.name ?? "")
        .trim()
        .toLowerCase() ===
      String(name ?? "")
        .trim()
        .toLowerCase(),
  );

export const getCertificateGenerateFields = (certName) => {
  const template = findStandardTemplateByName(certName);
  return CERT_GENERATE_FIELDS[template?.id] ?? [];
};

export const CERT_INSERT_FIELDS = [
  { name: "Pet Name", value: "{{PetName}}" },
  { name: "Owner Name", value: "{{OwnerName}}" },
  { name: "Pet Species", value: "{{PetSpecies}}" },
  { name: "Pet Breed", value: "{{PetBreed}}" },
  { name: "Pet Age", value: "{{PetAge}}" },
  { name: "Pet Sex", value: "{{PetSex}}" },
  { name: "Microchip No", value: "{{MicrochipNo}}" },
  { name: "Clinic Name", value: "{{ClinicName}}" },
  { name: "Vet Name", value: "{{VetName}}" },
  { name: "Date", value: "{{Date}}" },
];

export const STANDARD_CERTIFICATES = [
  {
    id: "std-vaccination",
    name: "Vaccination Certificate",
    subtitle: "Vaccination — Puppy",
    linkedServices: [
      { name: "Vaccination — Puppy", value: "Vaccination — Puppy" },
    ],
    kind: "standard",
    Icon: VaccinesIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has received the vaccination(s) as recorded herein.</p>",
  },
  {
    id: "std-rabies",
    name: "Rabies Certificate",
    subtitle: "Anti-Rabies Vaccination (Annual)",
    linkedServices: [
      {
        name: "Anti-Rabies Vaccination (Annual)",
        value: "Anti-Rabies Vaccination (Annual)",
      },
    ],
    kind: "standard",
    Icon: VolunteerActivismIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has been administered anti-rabies vaccination as recorded herein.</p>",
  },
  {
    id: "std-fitness",
    name: "Fitness / Health Certificate",
    subtitle: "General Health Checkup",
    linkedServices: [
      { name: "General Health Checkup", value: "General Health Checkup" },
    ],
    kind: "standard",
    Icon: FavoriteIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has been examined and found to be in good general health.</p>",
  },
  {
    id: "std-fit-travel",
    name: "Fit to Travel Certificate",
    subtitle: "Fit to Travel Exam",
    linkedServices: [
      { name: "Fit to Travel Exam", value: "Fit to Travel Exam" },
    ],
    kind: "standard",
    Icon: FlightIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has been examined and is fit to travel.</p>",
  },
  {
    id: "std-travel-export",
    name: "Travel / Export Health Certificate",
    subtitle: "International Travel Health Screening",
    linkedServices: [
      {
        name: "International Travel Health Screening",
        value: "International Travel Health Screening",
      },
    ],
    rabiesSourceService: {
      name: "Anti-Rabies Vaccination (Annual)",
      value: "Anti-Rabies Vaccination (Annual)",
    },
    parasiteSourceService: {
      name: "Deworming Treatment",
      value: "Deworming Treatment",
    },
    kind: "standard",
    Icon: PublicIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has undergone international travel health screening as recorded herein.</p>",
  },
  {
    id: "std-sterilization",
    name: "Sterilization Certificate",
    subtitle: "Spay / Neuter Surgery",
    linkedServices: [
      { name: "Spay / Neuter Surgery", value: "Spay / Neuter Surgery" },
    ],
    kind: "standard",
    Icon: ContentCutIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has undergone sterilization (spay / neuter) surgery.</p>",
  },
  {
    id: "std-euthanasia",
    name: "Euthanasia Certificate",
    subtitle: "Euthanasia",
    linkedServices: [{ name: "Euthanasia", value: "Euthanasia" }],
    kind: "standard",
    Icon: PetsIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, was humanely euthanized as recorded herein.</p>",
  },
  {
    id: "std-microchip",
    name: "Microchip Certificate",
    subtitle: "Microchip Implantation",
    linkedServices: [
      { name: "Microchip Implantation", value: "Microchip Implantation" },
    ],
    kind: "standard",
    Icon: MemoryIcon,
    body: "<p>This is to certify that <strong>{{PetName}}</strong>, belonging to <strong>{{OwnerName}}</strong>, has been implanted with microchip number <strong>{{MicrochipNo}}</strong>.</p>",
  },
];

export const loadCustomCertificates = () => {
  try {
    const raw = localStorage.getItem(CERT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const saveCustomCertificates = (list) => {
  localStorage.setItem(CERT_STORAGE_KEY, JSON.stringify(list ?? []));
};

/** Demo patient / details used for Plan → Certificates preview. */
export const CERT_PREVIEW_DEMO_PATIENT = {
  name: "Buddy",
  speciesBreed: "Canine (Dog) — Golden Retriever",
  sexAge: "Male, 4 Years",
  weightColour: "32 Kg — Golden",
  owner: "Rohan Mehta",
  ownerAddress: "On file at clinic",
  microchipNo: "Not recorded",
  dob: "Not recorded",
};

export const CERT_PREVIEW_CONFIGS = {
  "std-vaccination": {
    printTitle: "Certificate of Vaccination",
    certNo: "VC-2026-08421",
    patient: {
      name: "Stella",
      speciesBreed: "Canine (Dog) — Alaskan Husky",
      sexAge: "Female, 5 Years",
      weightColour: "5 Kg — Grey & White",
      owner: "Jay Pritchet",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: [
      "Vaccine",
      "Dose No.",
      "Batch No.",
      "Route",
      "Date Given",
      "Valid Until",
    ],
    detailRow: [
      "Abhayrab Human Rabies Vaccine Injection",
      "Annual Booster",
      "RB-1187",
      "IM",
      "16 Aug 2026",
      "16 Aug 2027",
    ],
    statement:
      "This is to certify that the above-named animal was examined and found to be in good health, and was vaccinated as detailed above by the undersigned veterinarian, in accordance with manufacturer guidelines and applicable local regulations.",
    vetName: "Dr. Jhon",
    vetLicense: "VET-88214",
    issuedDate: "16 Aug 2026",
  },
  "std-rabies": {
    printTitle: "Certificate of Rabies Vaccination",
    certNo: "RC-2026-04217",
    patient: {
      name: "Rocky",
      speciesBreed: "Canine (Dog) — Labrador Retriever",
      sexAge: "Male, 3 Years",
      weightColour: "28 Kg — Golden",
      owner: "Vivek Naik",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: [
      "Vaccine (Brand)",
      "Batch No.",
      "Date Administered",
      "Next Due Date",
      "Administering Vet",
    ],
    detailRow: [
      "Nobivac Rabies",
      "NBR-3390",
      "04 Feb 2026",
      "04 Feb 2027",
      "Dr. Anushka Lobo",
    ],
    statement:
      "This is to certify that the above-named animal has been vaccinated against Rabies as detailed above, in accordance with manufacturer guidelines and applicable local regulations. Revaccination is required on or before the due date stated above to maintain continued protection.",
    vetName: "Dr. Anushka Lobo",
    vetLicense: "VET-77031",
    issuedDate: "04 Feb 2026",
  },
  "std-fitness": {
    printTitle: "Certificate of Fitness / General Health",
    certNo: "HC-2026-06188",
    patient: {
      name: "Milo",
      speciesBreed: "Canine (Dog) — Beagle",
      sexAge: "Male, 2 Years",
      weightColour: "11 Kg — Tricolour",
      owner: "Priya Iyer",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: [
      "Purpose",
      "Exam Date",
      "Temperature",
      "Pulse",
      "Respiration",
      "Body Condition",
    ],
    detailRow: [
      "Boarding",
      "18 Aug 2026",
      "101.6 °F",
      "96 bpm",
      "22 rpm",
      "Ideal (5/9)",
    ],
    statement:
      "This is to certify that the above-named animal was examined on the date stated and found to be in good general health, with no clinical signs of infectious or contagious disease observed at the time of examination. This certificate reflects the animal's condition on the date of examination only.",
    vetName: "Dr. Jhon",
    vetLicense: "VET-88214",
    issuedDate: "18 Aug 2026",
  },
  "std-fit-travel": {
    printTitle: "Certificate of Fitness to Travel",
    certNo: "TC-2026-05902",
    patient: {
      name: "Coco",
      speciesBreed: "Feline (Cat) — Domestic Shorthair",
      sexAge: "Female, 4 Years",
      weightColour: "3.8 Kg — White",
      owner: "Sana Fernandes",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: ["Mode of Travel", "Exam Date", "Findings", "Valid Until"],
    detailRow: [
      "Domestic Air Travel (Cabin)",
      "20 Aug 2026",
      "No signs of illness, injury or distress",
      "30 Aug 2026",
    ],
    statement:
      "This is to certify that the above-named animal was examined within 10 days prior to the date of travel and found fit to travel by the mode of transport stated, showing no clinical signs of illness, injury, or distress at the time of examination.",
    vetName: "Dr. Anushka Lobo",
    vetLicense: "VET-77031",
    issuedDate: "20 Aug 2026",
  },
  "std-travel-export": {
    printTitle: "International Health Certificate for Travel / Export",
    certNo: "EC-2026-01175",
    patient: CERT_PREVIEW_DEMO_PATIENT,
    detailHeaders: [
      "Destination Country",
      "Mode of Transport",
      "Rabies Vaccination",
      "Parasite Treatment",
      "Microchip No.",
    ],
    detailRow: [
      "United Kingdom",
      "Air Cargo",
      "Nobivac Rabies, 04 Feb 2026",
      "Fenbendazole, 12 Aug 2026",
      "900215001234567",
    ],
    statement:
      "This is to certify that the above-named animal was examined and found free from clinical signs of infectious or contagious disease, and that the vaccination, parasite treatment, and identification details stated above are accurate as per veterinary records maintained at this clinic. This certificate is issued in support of the animal's travel to the destination stated and may require further endorsement by the relevant government veterinary or quarantine authority prior to travel, in accordance with destination-country import requirements.",
    vetName: "Dr. Jhon",
    vetLicense: "VET-88214",
    issuedDate: "19 Aug 2026",
  },
  "std-sterilization": {
    printTitle: "Certificate of Sterilization",
    certNo: "SC-2026-08533",
    patient: {
      name: "Bruno",
      speciesBreed: "Canine (Dog) — Indie",
      sexAge: "Male, 2 Years",
      weightColour: "18 Kg — Brown & White",
      owner: "Kavya Menon",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: ["Procedure", "Method", "Date Performed", "Operating Vet"],
    detailRow: [
      "Orchiectomy (Neuter)",
      "Standard surgical castration",
      "19 Aug 2026",
      "Dr. Jhon",
    ],
    statement:
      "This is to certify that the above-named animal was examined and found fit for surgery, and that the sterilization procedure detailed above was performed by the undersigned veterinarian. Sterilization is permanent and irreversible.",
    vetName: "Dr. Jhon",
    vetLicense: "VET-88214",
    issuedDate: "19 Aug 2026",
  },
  "std-euthanasia": {
    printTitle: "Certificate of Euthanasia",
    certNo: "EU-2026-00341",
    patient: {
      name: "Simba",
      speciesBreed: "Feline (Cat) — Domestic Shorthair",
      sexAge: "Male, 16 Years",
      weightColour: "3.2 Kg — Orange",
      owner: "Arjun Singh",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: [
      "Date & Time",
      "Reason",
      "Method",
      "Disposition of Remains",
    ],
    detailRow: [
      "21 Aug 2026, 11:30 am",
      "Advanced chronic illness; quality of life decline",
      "Intravenous injection of an approved euthanasia agent, per veterinary guidelines",
      "Returned to owner",
    ],
    statement:
      "This is to certify that the above-named animal was humanely euthanised by the undersigned veterinarian, in accordance with accepted veterinary standards, at the informed request and consent of the owner named above.",
    vetName: "Dr. Anushka Lobo",
    vetLicense: "VET-77031",
    issuedDate: "21 Aug 2026",
  },
  "std-microchip": {
    printTitle: "Certificate of Microchip Implantation",
    certNo: "MC-2026-02890",
    patient: {
      name: "Whiskers",
      speciesBreed: "Feline (Cat) — Persian",
      sexAge: "Female, 1 Year",
      weightColour: "3.5 Kg — White",
      owner: "Neha Kapoor",
      ownerAddress: "On file at clinic",
      microchipNo: "Not recorded",
      dob: "Not recorded",
    },
    detailHeaders: [
      "Microchip No.", //(ISO 11784/11785)
      "Date Implanted",
      "Site of Implantation",
      "Manufacturer / Brand",
    ],
    detailRow: [
      "900215001987654",
      "17 Aug 2026",
      "Subcutaneous, dorsal midline between scapulae",
      "HomeAgain",
    ],
    statement:
      "This is to certify that the above-named animal has been implanted with a microchip as detailed above, and that the chip number has been verified by scanner at the time of implantation.",
    vetName: "Dr. Jhon",
    vetLicense: "VET-88214",
    issuedDate: "17 Aug 2026",
  },
};

export const getCertificatePreviewConfig = (cert) => {
  if (!cert) return null;
  const live = cert?.previewData ?? null;
  const base = CERT_PREVIEW_CONFIGS[cert.id];
  if (base) {
    const detailRow = [...(base.detailRow ?? [])];
    if (cert.id === "std-travel-export") {
      if (cert?.rabiesSourceService?.name) {
        detailRow[2] = `${cert.rabiesSourceService.name}, 04 Feb 2026`;
      }
      if (cert?.parasiteSourceService?.name) {
        detailRow[3] = `${cert.parasiteSourceService.name}, 12 Aug 2026`;
      }
    }
    return {
      ...base,
      detailRow: live?.detailRow ?? detailRow,
      patient: live?.patient ?? base.patient,
      certNo: live?.certNo ?? base.certNo,
      vetName: live?.vetName ?? base.vetName,
      vetLicense: live?.vetLicense ?? base.vetLicense,
      issuedDate: live?.issuedDate ?? base.issuedDate,
      note: live?.note ?? cert?.note,
      footerNote: live?.note ?? cert?.note ?? DEFAULT_FOOTER_NOTE,
      printTitle: base.printTitle,
      statement: base.statement,
    };
  }
  return {
    printTitle: cert?.name || "Custom Certificate",
    certNo: live?.certNo ?? "CC-PREVIEW",
    patient: live?.patient ?? CERT_PREVIEW_DEMO_PATIENT,
    detailHeaders: live?.detailHeaders ?? null,
    detailRow: live?.detailRow ?? null,
    statementHtml: cert?.body || "",
    statement:
      "This is to certify that the above-named animal has been examined and the details recorded herein are accurate as per veterinary records maintained at this clinic.",
    note: live?.note ?? cert?.note ?? cert?.footerNote ?? DEFAULT_FOOTER_NOTE,
    vetName: live?.vetName ?? "Dr. Jhon",
    vetLicense: live?.vetLicense ?? "VET-88214",
    issuedDate: live?.issuedDate ?? "19 Aug 2026",
    footerNote:
      live?.note ?? cert?.note ?? cert?.footerNote ?? DEFAULT_FOOTER_NOTE,
  };
};
